import { injectable, inject } from 'inversify';
import type { IFluoroCurveController, IFluoroCurveControllerFactory } from './interfaces';
import { FluoroCurveController, NullFluoroCurveController } from './FluoroCurveController';
import { TYPES } from '@/engine/ioc';
import type { ViewsService } from '@/engine/app/Views.service';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { ViewController } from '@/engine/app/View.controller';
import { ViewPortKind } from '@/engine/app/interfaces';
import type { ViewEntity } from '@/engine/app/models/viewEntity/view.entity.ts';

@injectable()
export class FluoroCurveControllerFactory implements IFluoroCurveControllerFactory {
  private readonly _viewsService: ViewsService;

  private readonly _viewController: ViewController;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.ViewController) viewController: ViewController,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm
  ) {
    this._viewsService = viewsService;
    this._viewController = viewController;
    this._planningAlgorithm = planningAlgorithm;
  }

  public createController(view: ViewEntity): IFluoroCurveController {
    const viewPort = view.getViewPorts().find((vp) => vp.kind === ViewPortKind.ThreeD);
    if (!viewPort) {
      throw new Error('View Port is undefined');
    }
    const cameraController = this._viewController.getViewPortController(viewPort.descriptor)?.getCameraController();
    if (!cameraController) {
      throw new Error('Camera Controller is undefined');
    }
    const planeDescriptor = view.getFluoroCurvePlaneDescriptor();
    if (planeDescriptor === undefined) {
      return new NullFluoroCurveController();
    } else {
      return new FluoroCurveController(cameraController, this._planningAlgorithm, planeDescriptor);
    }
  }
}
